import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Building2, User, ArrowRight, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import { setAuthSession } from '@shared/auth/session';
import Button from '@shared/ui/Button';
import BrandLockup from '../components/BrandLockup';
import AuthField from '../components/auth/AuthField';
import AccountCredentialFields from '../components/auth/AccountCredentialFields';
import { credentialFields, passwordsMatch } from '../components/auth/credentialSchema';

const signupSchema = z.object({
  // Organization details
  organizationName: z.string().min(1, "Organization name is required"),
  orgCategory: z.string().optional(),

  // Contact
  email: z.string().email("Valid email is required"),

  // Shared credential rules — these mirror the route validators, so the client
  // can no longer accept something the server rejects. (This schema's own
  // username rule had no charset regex, while POST /auth/signup requires
  // /^[a-z0-9_]+$/, so "John Smith" passed here and 400ed there.)
  ...credentialFields,
}).refine(passwordsMatch.check, passwordsMatch.options);

type SignupFormData = z.infer<typeof signupSchema>;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Get source parameter from URL
  const urlParams = new URLSearchParams(location.search);
  const source = urlParams.get('source') || 'default';

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      organizationName: "",
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      password: "",
      confirmPassword: "",
    },
  });

  const signupMutation = useMutation({
    mutationFn: async (data: SignupFormData) => {
      const response = await api.post('/auth/signup', { ...data, source });
      return response.data;
    },
    onSuccess: (data) => {
      setAuthSession({
        token: data.data.token,
        user: data.data.user,
        organization: data.data.organization,
        membership: data.data.membership,
        includeRoleFromMembership: true,
      });
      // Force refetch user data before redirecting to ensure auth state is updated
      queryClient.invalidateQueries({ queryKey: ["/auth/user"] });
      setTimeout(() => navigate("/dashboard"), 100); // Redirect to dashboard after signup
    },
    onError: (error: Error) => {
      console.error("Signup Failed:", error.message || "An error occurred during signup. Please try again.");
    },
  });

  const onSubmit = (data: SignupFormData) => {
    signupMutation.mutate(data);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border-light dark:border-border-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <Link to="/" className="flex items-center space-x-3">
              <BrandLockup />
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary">
                Already have an account? Sign In
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-text-primary dark:text-text-dark-primary">Start Your Free Trial</h2>
            <p className="mt-2 text-text-secondary dark:text-text-dark-secondary">
              Create your organization and account to begin your legislative tracking journey
            </p>
          </div>

        <div className="card">
            <div className="p-6">
              <h3 className="text-2xl text-center font-bold text-text-primary dark:text-text-dark-primary mb-6">Create Your Organization</h3>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Organization Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                    <Building2 className="h-5 w-5 text-primary-700" aria-hidden="true" />
                    <span>Organization Details</span>
                  </div>
                  
                  <div className="space-y-4 pl-7">
                    <div>
                      <label htmlFor="organizationName" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                        Organization Name <span className="text-error-600" aria-label="required">*</span>
              </label>
              <input
                        id="organizationName"
                type="text"
                        placeholder="Your Organization Name"
                        className={`input ${form.formState.errors.organizationName ? "border-red-500" : ""}`}
                        aria-required="true"
                        aria-invalid={form.formState.errors.organizationName ? 'true' : 'false'}
                        aria-describedby={form.formState.errors.organizationName ? 'organizationName-error' : undefined}
                        {...form.register("organizationName")}
                      />
                      {form.formState.errors.organizationName && (
                        <p 
                          id="organizationName-error"
                          className="text-sm text-red-600 mt-1"
                          role="alert"
                        >
                          {form.formState.errors.organizationName.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="orgCategory" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                        Organization Category
                      </label>
                      <select
                        id="orgCategory"
                className="input"
                        onChange={(e) => form.setValue("orgCategory", e.target.value)}
                      >
                        <option value="">Select organization category</option>
                        <option value="Association">Association</option>
                        <option value="Non-profit">Non-profit</option>
                        <option value="Business">Business</option>
                        <option value="Advocacy Group">Advocacy Group</option>
                        <option value="Political Candidate / Politician">Political Candidate / Politician</option>
                        <option value="Political Action Committee (PAC)">Political Action Committee (PAC)</option>
                        <option value="Media Organization">Media Organization</option>
                        <option value="Lobbyist or Lobbying Firm">Lobbyist or Lobbying Firm</option>
                        <option value="Government Agency">Government Agency</option>
                        <option value="Educational Institution">Educational Institution</option>
                        <option value="Other">Other (please specify)</option>
                      </select>
                      <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary mt-1">
                        Optional: Select the category that best describes your organization
                      </p>
                    </div>
                  </div>
                </div>

                {/* User Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                    <User className="h-5 w-5 text-primary-700" />
                    <span>Your Account Details</span>
                  </div>
                  
                  <div className="space-y-4 pl-7">
                    <AuthField
                      id="email"
                      label="Email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="you@example.org"
                      registration={form.register('email')}
                      error={
                        typeof form.formState.errors.email?.message === 'string'
                          ? form.formState.errors.email.message
                          : undefined
                      }
                    />
                    <AccountCredentialFields
                      register={form.register}
                      errors={form.formState.errors}
                    />
                  </div>
                </div>

                {/* What You Get */}
                <div className="bg-background-tertiary dark:bg-background-dark-tertiary p-4 rounded-lg">
                  <h3 className="font-semibold mb-3 text-text-primary dark:text-text-dark-primary">What you&apos;ll get with your free trial:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-primary-700" />
                      <span className="text-sm">Access to shared workspaces</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-primary-700" />
                      <span className="text-sm">Basic bill tracking</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-primary-700" />
                      <span className="text-sm">Up to 5 team members</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-primary-700" />
                      <span className="text-sm">Email notifications</span>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  variant="primary"
              className="w-full" 
                  disabled={signupMutation.isLoading}
                >
                  {signupMutation.isLoading ? (
                    "Creating Your Account..."
                  ) : (
                    <>
                      Start 7 Day Free Trial
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary text-center">
                  By creating an account, you agree to our{' '}
                  <a 
                    href="https://capitolcommons.ai/terms" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a 
                    href="https://capitolcommons.ai/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Privacy Policy
                  </a>.
                  You can upgrade to a paid plan at any time.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage; 